import { observer } from "mobx-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/i18n";

/**
 * Confirmation modal for revoking a user's grant to an OAuth app.
 *
 * Used on the user's Connected apps profile screen. Revocation deletes
 * the OAuthConsent row server-side; outstanding access tokens stop
 * working at next introspection but technically remain in the user's
 * cookies/clipboard until they expire — surfaced in help text.
 */
export const RevokeAppModal = observer(function RevokeAppModal({
  appName,
  isOpen,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  appName: string;
  isOpen: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("oauth_apps.revoke.title")}</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{appName}</span>{" "}
            {t("oauth_apps.revoke.description_after")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? t("oauth_apps.revoke.revoking") : t("oauth_apps.revoke.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
